/**
 * Constants concerning REST APIs used in the Messaging app.
 * @type {Object}
 */
export const MESSAGING_API_CONSTANTS = {
  // Optional parameter to listConversations API: The maximum number of conversations to retrieve. Default is 20, Max is 50.
  LIST_CONVERSATION_API_NUM_CONVERSATIONS_LIMIT: 20,
  // Optional parameter to listConversationEntries API: The maximum number of entries to retrieve. Default is 20, Max is 50.
  LIST_CONVERSATION_ENTRIES_API_ENTRIES_LIMIT: 50,
} as const;

/**
 * Constants concerning a conversation in the Messaging app.
 * @type {Object}
 */
export const CONVERSATION_CONSTANTS = {
  /**
   * Properties of a generic ConversationEntry class instance.
   * @type {String}
   */
  ConversationEntryProperties: {
    ACTOR_NAME: 'actorName',
    ACTOR_TYPE: 'actorType',
    CONTENT: 'content',
    CONVERSATION_ID: 'conversationId',
    ENTRY_TYPE: 'entryType',
    MESSAGE_ID: 'messageId',
    MESSAGE_REASON: 'messageReason',
    MESSAGE_TYPE: 'messageType',
    SENDER: 'sender',
    TRANSCRIPTED_TIMESTAMP: 'transcriptedTimestamp',
  },

  /**
   * Status of a conversation in the chat client.
   * @type {String}
   */
  ConversationStatus: {
    CLOSED_CONVERSATION: 'CLOSED',
    NOT_STARTED_CONVERSATION: 'NOT_STARTED',
    OPENED_CONVERSATION: 'OPEN',
  },

  /**
   * Types of conversation entries.
   * @type {String}
   */
  EntryTypes: {
    CLOSE_CONVERSATION: 'CloseConversation',
    CONVERSATION_MESSAGE: 'Message',
    DELIVERY: 'Delivery',
    DELIVERY_ACKNOWLEDGEMENT: 'DeliveryAcknowledgement',
    PARTICIPANT_CHANGED: 'ParticipantChanged',
    READ: 'Read',
    READ_ACKNOWLEDGEMENT: 'ReadAcknowledgement',
    ROUTING_RESULT: 'RoutingResult',
    ROUTING_WORK_RESULT: 'RoutingWorkResult',
    TYPING_STARTED_INDICATOR: 'TypingStartedIndicator',
    TYPING_STOPPED_INDICATOR: 'TypingStoppedIndicator',
  },

  /**
   * Types of events published by Conversation Service in SCRT 2.0.
   * Refer https://developer.salesforce.com/docs/service/messaging-api/references/about/server-sent-events-structure.html
   * @type {String}
   */
  EventTypes: {
    CONVERSATION_CLOSE_CONVERSATION: 'CONVERSATION_CLOSE_CONVERSATION',
    CONVERSATION_DELIVERY_ACKNOWLEDGEMENT:
      'CONVERSATION_DELIVERY_ACKNOWLEDGEMENT',
    CONVERSATION_MESSAGE: 'CONVERSATION_MESSAGE',
    CONVERSATION_PARTICIPANT_CHANGED: 'CONVERSATION_PARTICIPANT_CHANGED',
    CONVERSATION_READ_ACKNOWLEDGEMENT: 'CONVERSATION_READ_ACKNOWLEDGEMENT',
    CONVERSATION_ROUTING_RESULT: 'CONVERSATION_ROUTING_RESULT',
    CONVERSATION_ROUTING_WORK_RESULT: 'CONVERSATION_ROUTING_WORK_RESULT',
    CONVERSATION_TYPING_STARTED_INDICATOR:
      'CONVERSATION_TYPING_STARTED_INDICATOR',
    CONVERSATION_TYPING_STOPPED_INDICATOR:
      'CONVERSATION_TYPING_STOPPED_INDICATOR',
  },

  /**
   * The rendering format of a Message entry type, i.e. how it looks.
   * Refer https://developer.salesforce.com/docs/service/messaging-api/references/about/message-types-format-types.html#format-types
   * @type {String}
   */
  FormatTypes: {
    ATTACHMENTS: 'Attachments',
    BUTTONS: 'Buttons',
    QUICK_REPLIES: 'QuickReplies',
    RICH_LINK: 'RichLink',
    SELECTIONS: 'Selections',
    TEXT: 'Text',
  },

  /**
   * The semantic type of a Message entry type, i.e. what a message does.
   * Refer https://developer.salesforce.com/docs/service/messaging-api/references/about/message-types-format-types.html#message-types
   * @type {String}
   */
  MessageTypes: {
    CHOICES_MESSAGE: 'ChoicesMessage',
    CHOICES_RESPONSE_MESSAGE: 'ChoicesResponseMessage',
    FORM_MESSAGE: 'FormMessage',
    STATIC_CONTENT_MESSAGE: 'StaticContentMessage',
  },

  /**
   * The operation (i.e. join/leave) of the ParticipantChanged entry type.
   * @type {String}
   */
  ParticipantChangedOperations: {
    ADD: 'add',
    REMOVE: 'remove',
  },

  /**
   * The role of the sender of events published by Conversation Service in SCRT 2.0.
   * @type {String}
   */
  ParticipantRoles: {
    AGENT: 'Agent',
    CHATBOT: 'Chatbot',
    ENDUSER: 'EndUser',
    ROUTER: 'Router',
    SUPERVISOR: 'Supervisor',
    SYSTEM: 'System',
  },

  /**
   * How routing failed or succeeded, sent in RoutingResult entries.
   * @type {String}
   */
  RoutingFailureTypes: {
    NO_ERROR: 'None',
    ROUTING_ERROR: 'RoutingError',
    SUBMISSION_ERROR: 'SubmissionError',
    UNKNOWN_ERROR: 'Unknown',
  },

  /**
   * The current routing status of the conversation, returned from the ConversationStatus API.
   * @type {String}
   */
  RoutingStatus: {
    INITIAL: 'INITIAL',
    NEEDS_ROUTING: 'NEEDS_ROUTING',
    ROUTED: 'ROUTED',
    TRANSFER: 'TRANSFER',
  },

  /**
   * The routing type, set from the RoutingContext and sent in RoutingResult entries.
   * @type {String}
   */
  RoutingTypes: {
    INITIAL: 'Initial',
    TRANSFER: 'Transfer',
  },
} as const;

/**
 * Update this list to add/remove support for Entry types in the app. This can be used to render supported conversation entries, to be retrived from listConversationEntries API etc.
 * @type {Array}
 */
export const SUPPORTED_ENTRY_TYPES = [
  CONVERSATION_CONSTANTS.EntryTypes.CONVERSATION_MESSAGE,
  CONVERSATION_CONSTANTS.EntryTypes.PARTICIPANT_CHANGED,
  CONVERSATION_CONSTANTS.EntryTypes.ROUTING_RESULT,
] as const;

/**
 * Messaging app's functional constants.
 * @type {Object}
 */
export const APP_CONSTANTS = {
  APP_CAPABILITIES_VERSION: '1',
  APP_PLATFORM: 'Web',
  HTTPS_PROTOCOL: 'https:',
  ORGANIZATION_ID_PREFIX: '00D',
  SALESFORCE_MESSAGING_SCRT_URL: 'salesforce-scrt.com',
  // The storage key name under which messaging data is stored in local/session storage.
  WEB_STORAGE_KEY: 'MESSAGING_SAMPLE_APP_WEB_STORAGE_',
} as const;

/**
 * Constants related to Embedded Service Deployment configuration.
 * @type {object}
 */
export const DEPLOYMENT_CONFIGURATION_CONSTANTS = {
  PRECHAT_DISPLAY_EVERY_CONVERSATION: 'Conversation',
  PRECHAT_DISPLAY_EVERY_SESSION: 'Session',
  SUPPORTED_PRECHAT_FORM_FIELDS: {
    CHECKBOX: 'Checkbox',
    CHOICELIST: 'ChoiceList',
    EMAIL: 'Email',
    NUMBER: 'Number',
    PHONE: 'Phone',
    TEXT: 'Text',
  },
} as const;

/**
 * Messaging app's client-side constants.
 * @type {Object}
 */
export const CLIENT_CONSTANTS = {
  TYPING_INDICATOR_DISPLAY_TIMEOUT: 5000,
} as const;
